import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useStudioStore, useAuthStore } from '@/store'
import { getSupabase } from '@/lib/supabase'
import { withAuthRecovery } from '@/lib/authRecovery'
import { StudioAnalytics, CalendarDay, MetricPeriod } from '@/types'
import { Card } from '@/components/ui'
import { LoadingScreen } from '@/components/LoadingScreen'
import { ManagementCompanyNavigation } from '@/components'
import { getLocalIsoDate } from '@/lib/analytics/periodCalculations'
import { 
  TrendingUp, 
  AlertTriangle, 
  CheckCircle, 
  Calendar,
  Filter,
  Download,
  Search,
  BarChart3,
  ChevronLeft,
  Building2
} from 'lucide-react'

interface AnalyticsFilters {
  categoryFilters: ('fully_confirmed' | 'fully_filled' | 'with_gaps' | 'never_filled' | 'no_plan')[]
  sortBy: 'studio_number' | 'completion_percentage' | 'quality_score' | 'last_entry_date'
  sortOrder: 'asc' | 'desc'
}

export function ManagementAnalyticsPage() {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { isManagementCompany, setSelectedStudio, studios } = useStudioStore()
  
  const [loading, setLoading] = useState(true)
  const [studiosData, setStudiosData] = useState<StudioAnalytics[]>([])
  const [filteredData, setFilteredData] = useState<StudioAnalytics[]>([])
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [filters, setFilters] = useState<AnalyticsFilters>({
    categoryFilters: ['fully_confirmed', 'fully_filled', 'with_gaps'],
    sortBy: 'completion_percentage',
    sortOrder: 'asc'
  })
  const [calendarData, setCalendarData] = useState<CalendarDay[]>([])
  const [calendarLoading, setCalendarLoading] = useState(true)
  const [selectedDate, setSelectedDate] = useState<string | undefined>()
  const [selectedPeriod, setSelectedPeriod] = useState<MetricPeriod | null>(null)
  const [noPlanStudioIds, setNoPlanStudioIds] = useState<number[]>([])

  // Защита доступа
  useEffect(() => {
    if (!isManagementCompany()) {
      navigate('/')
      return
    }
  }, [isManagementCompany, navigate])

  // Загрузка текущего периода при монтировании
  useEffect(() => {
    if (user?.id && isManagementCompany()) {
      loadCurrentPeriod()
      return
    }
    setLoading(false)
  }, [user?.id, isManagementCompany])

  // Загрузка данных при изменении периода
  useEffect(() => {
    if (selectedPeriod && user?.id && isManagementCompany()) {
      loadAnalyticsData()
      loadCalendarData()
    }
  }, [selectedPeriod, user?.id, isManagementCompany])

  // Применение фильтров
  useEffect(() => {
    applyFilters()
  }, [studiosData, filters, searchTerm, noPlanStudioIds, selectedPeriod])

  // Студии без плана на период (логика как в "Цифре дня": planRevenue <= 0)
  useEffect(() => {
    if (!selectedPeriod) {
      setNoPlanStudioIds([])
      return
    }

    loadNoPlanStudios(selectedPeriod.period_id, studiosData)
  }, [selectedPeriod, studiosData])

  const loadCurrentPeriod = async () => {
    try {
      const supabase = getSupabase()
      const today = getLocalIsoDate()

      const { data, error } = await withAuthRecovery(() =>
        supabase
          .from('metric_periods')
          .select('*')
          .lte('period_start', today)
          .gte('period_end', today)
          .single()
      ) as { data: MetricPeriod | null, error: any }
      
      if (error) {
        console.error('Error loading current period:', error)
        setError('Не удалось определить текущий период')
        setLoading(false)
        return
      }
      
      if (data) {
        setSelectedPeriod(data)
        setSelectedDate(today)
        return
      }
      setError('Текущий период не найден')
      setLoading(false)
    } catch (err) {
      console.error('Error loading current period:', err)
      setError('Не удалось определить текущий период')
      setLoading(false)
    }
  }

  const loadAnalyticsData = async () => {
    if (!user?.id || !selectedPeriod) return

    try {
      setLoading(true)
      setError(null)
      console.log('🔄 Loading management company analytics for period:', selectedPeriod.period_id)

      const supabase = getSupabase()
      const { data, error: analyticsError } = await withAuthRecovery(() =>
        (supabase as any)
          .rpc('get_studios_data_analytics_v2', {
            p_user_id: user.id,
            p_period_id: selectedPeriod.period_id
          })
      )

      if (analyticsError) {
        throw new Error(`Analytics error: ${analyticsError.message}`)
      }

      setStudiosData(data || [])
      console.log('✅ Analytics data loaded:', data?.length || 0, 'studios')

    } catch (err) {
      console.error('❌ Error loading analytics:', err)
      setError(err instanceof Error ? err.message : 'Ошибка загрузки аналитики')
    } finally {
      setLoading(false)
    }
  }

  const loadCalendarData = async () => {
    if (!user?.id || !selectedPeriod) return

    try {
      setCalendarLoading(true)
      console.log('🔄 Loading calendar data for period:', selectedPeriod.period_start, '-', selectedPeriod.period_end)

      const supabase = getSupabase()
      const { data, error: calendarError } = await withAuthRecovery(() =>
        (supabase as any)
          .rpc('get_calendar_analytics_data_v2', {
            p_user_id: user.id,
            p_period_id: selectedPeriod.period_id
          })
      )

      if (calendarError) {
        throw new Error(`Calendar error: ${calendarError.message}`)
      }

      // Преобразуем данные из SQL функции в нужный формат
      const calendarDays: CalendarDay[] = (data || []).map((item: any) => ({
        date: item.date,
        studiosWithData: item.studios_with_data,
        totalStudios: item.total_studios,
        completionRate: item.completion_rate,
        pendingConfirmations: item.pending_confirmations,
        issues: item.issues || []
      }))

      setCalendarData(calendarDays)
      console.log('✅ Calendar data loaded:', calendarDays.length, 'days')

    } catch (err) {
      console.error('❌ Error loading calendar data:', err)
    } finally {
      setCalendarLoading(false)
    }
  }

  const loadNoPlanStudios = async (periodId: number, studios: StudioAnalytics[]) => {
    if (studios.length === 0) {
      setNoPlanStudioIds([])
      return
    }

    try {
      const supabase = getSupabase()
      const studioIds = studios.map(studio => studio.studio_internal_id)

      const { data, error } = await supabase
        .from('studio_weekly_plan_vs_fact')
        .select('studio_internal_id, weekly_gross_revenue_plan')
        .eq('period_id', periodId)
        .in('studio_internal_id', studioIds)

      if (error) {
        throw new Error(error.message)
      }

      const totalsByStudio = new Map<number, number>()
      ;(data || []).forEach((row: any) => {
        const studioId = row.studio_internal_id as number
        const currentTotal = totalsByStudio.get(studioId) || 0
        totalsByStudio.set(studioId, currentTotal + (row.weekly_gross_revenue_plan || 0))
      })

      const studiosWithoutPlan = studioIds.filter((studioId) => {
        const revenuePlan = totalsByStudio.get(studioId)
        return !revenuePlan || revenuePlan <= 0
      })

      setNoPlanStudioIds(studiosWithoutPlan)
    } catch (err) {
      console.error('❌ Error loading no-plan studios:', err)
      setNoPlanStudioIds([])
    }
  }

  const getTotalDaysInPeriod = (period: MetricPeriod | null) => {
    if (!period) return 0

    const today = new Date()
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)

    const periodStart = new Date(period.period_start)
    const periodEnd = new Date(period.period_end)

    // Анализируем до вчера или до конца периода (что раньше)
    const analysisEnd = yesterday < periodEnd ? yesterday : periodEnd

    if (analysisEnd < periodStart) return 0

    return Math.floor((analysisEnd.getTime() - periodStart.getTime()) / (1000 * 60 * 60 * 24)) + 1
  }

  // Получить все даты в периоде
  const getDatesInPeriod = () => {
    if (!selectedPeriod) return []
    
    const dates: string[] = []
    const start = new Date(selectedPeriod.period_start)
    const end = new Date(selectedPeriod.period_end)
    
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      dates.push(d.toISOString().split('T')[0])
    }
    
    return dates
  }

  const getStatusForDay = (date: string): 'confirmed' | 'partial' | 'missing' | 'none' => {
    const dayData = calendarData.find(item => item.date === date)
    
    if (!dayData || dayData.studiosWithData === 0) {
      return 'none' // Нет данных вообще
    }
    
    const { studiosWithData, totalStudios, pendingConfirmations } = dayData
    
    // Все открытые студии заполнили данные
    if (studiosWithData === totalStudios) {
      // Все подтвердили
      if (pendingConfirmations === 0) {
        return 'confirmed' // Зеленый - все подтвердили
      } else {
        return 'partial' // Желтый - все внесли, но не все подтвердили
      }
    } else {
      return 'missing' // Красный - не все заполнили данные
    }
  }

  const getStudioDayBreakdown = (studio: StudioAnalytics, totalDays: number) => {
    const normalizedTotalDays = Math.max(0, totalDays)
    const confirmedDays = Math.min(Math.max(0, studio.confirmed_days || 0), normalizedTotalDays)
    const rawSavedDays = Math.max(0, studio.saved_days || 0)
    const savedDays = Math.min(
      Math.max(rawSavedDays, confirmedDays),
      normalizedTotalDays
    )
    const filledUnconfirmedDays = Math.max(0, savedDays - confirmedDays)
    const missingDays = Math.max(0, normalizedTotalDays - confirmedDays - filledUnconfirmedDays)

    return {
      totalDays: normalizedTotalDays,
      confirmedDays,
      savedDays,
      filledUnconfirmedDays,
      missingDays
    }
  }

  const applyFilters = () => {
    let filtered = [...studiosData]
    const noPlanSet = new Set(noPlanStudioIds)
    const totalDaysInPeriod = getTotalDaysInPeriod(selectedPeriod)

    // Поиск по названию
    if (searchTerm) {
      filtered = filtered.filter(studio => 
        studio.studio_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        studio.city_name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    // Фильтр по категориям студий (непересекающиеся множества)
    if (filters.categoryFilters.length > 0) {
      filtered = filtered.filter(studio => {
        const breakdown = getStudioDayBreakdown(studio, totalDaysInPeriod)

        const hasNoPlan = noPlanSet.has(studio.studio_internal_id)
        const hasNoPlanFilter = filters.categoryFilters.includes('no_plan')
        const otherFilters = filters.categoryFilters.filter(c => c !== 'no_plan')

        const matchesOtherFilters = otherFilters.length === 0 || otherFilters.some(category => {
          switch (category) {
            case 'fully_confirmed':
              // Полностью подтверждены (все даты подтверждены)
              return breakdown.confirmedDays >= breakdown.totalDays && studio.confirmation_percentage === 100
            case 'fully_filled':
              // Полностью заполнены (был save во все дни), но НЕ полностью подтверждены
              return breakdown.savedDays >= breakdown.totalDays && breakdown.confirmedDays < breakdown.totalDays
            case 'with_gaps':
              // Есть пропуски: save был не во все дни периода, но хотя бы в один день был
              return breakdown.savedDays > 0 && breakdown.savedDays < breakdown.totalDays
            case 'never_filled':
              // Вообще не нажимали "Сохранить изменения" за период
              return breakdown.savedDays === 0
            default:
              return false
          }
        })

        // "Нет плана" работает как дополнительный фильтр (И) к остальным категориям.
        if (hasNoPlanFilter) {
          return hasNoPlan && matchesOtherFilters
        }

        return matchesOtherFilters
      })
    }

    // Сортировка
    filtered.sort((a, b) => {
      let aValue: any
      let bValue: any

      if (filters.sortBy === 'completion_percentage') {
        const aBreakdown = getStudioDayBreakdown(a, totalDaysInPeriod)
        const bBreakdown = getStudioDayBreakdown(b, totalDaysInPeriod)
        const direction = filters.sortOrder === 'asc' ? -1 : 1
        const priorityComparisons = [
          (aBreakdown.missingDays - bBreakdown.missingDays) * direction,
          (aBreakdown.filledUnconfirmedDays - bBreakdown.filledUnconfirmedDays) * direction,
          (aBreakdown.confirmedDays - bBreakdown.confirmedDays) * direction,
          a.city_name.localeCompare(b.city_name, 'ru'),
          a.studio_number.localeCompare(b.studio_number, 'ru')
        ]

        const firstNonZero = priorityComparisons.find((comparison) => comparison !== 0)
        return firstNonZero ?? 0
      } else {
        aValue = a[filters.sortBy]
        bValue = b[filters.sortBy]
      }

      // Обработка дат
      if (filters.sortBy === 'last_entry_date') {
        aValue = aValue ? new Date(aValue).getTime() : 0
        bValue = bValue ? new Date(bValue).getTime() : 0
      }

      if (aValue === bValue) return 0
      
      const comparison = aValue > bValue ? 1 : -1
      return filters.sortOrder === 'asc' ? comparison : -comparison
    })

    setFilteredData(filtered)
  }

  const getCompletionColor = (percentage: number) => {
    if (percentage >= 90) return 'text-green-700 bg-green-100'
    if (percentage >= 70) return 'text-yellow-700 bg-yellow-100'
    if (percentage >= 50) return 'text-orange-700 bg-orange-100'
    return 'text-red-700 bg-red-100'
  }

  const getQualityColor = (score: number) => {
    if (score >= 90) return 'text-emerald-700 bg-emerald-100'
    if (score >= 70) return 'text-blue-700 bg-blue-100'
    if (score >= 50) return 'text-yellow-700 bg-yellow-100'
    return 'text-red-700 bg-red-100'
  }

  const exportData = () => {
    // Простой экспорт в CSV
    const headers = ['Студия', 'Город', 'Заполнено %', 'Подтверждено %', 'Качество', 'Последняя запись']
    const rows = filteredData.map(studio => [
      studio.studio_number,
      studio.city_name,
      studio.completion_percentage.toFixed(1),
      studio.confirmation_percentage.toFixed(1),
      studio.quality_score.toString(),
      studio.last_entry_date || 'Нет данных'
    ])

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `analytics_period_${selectedPeriod?.period_id || 'export'}.csv`
    link.click()
  }

  if (loading) {
    return <LoadingScreen />
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-lg font-medium mb-2">Ошибка загрузки</h2>
          <p className="text-muted-foreground mb-4">{error}</p>
          <button 
            onClick={loadAnalyticsData}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
          >
            Попробовать снова
          </button>
        </Card>
      </div>
    )
  }

  // Вычисляем количество дней в периоде (для summary stats)
  const totalDaysInPeriod = getTotalDaysInPeriod(selectedPeriod)
  const noPlanStudioIdsSet = new Set(noPlanStudioIds)

  const summaryStats = {
    allConfirmed: studiosData.filter(s => {
      // Студии у которых подтверждены все даты с 1 числа по вчера
      const breakdown = getStudioDayBreakdown(s, totalDaysInPeriod)
      return breakdown.confirmedDays >= breakdown.totalDays && s.confirmation_percentage === 100
    }).length,
    allFilled: studiosData.filter(s => {
      // Save был в каждый день периода до вчера, но не всё подтверждено
      const breakdown = getStudioDayBreakdown(s, totalDaysInPeriod)
      return breakdown.savedDays >= breakdown.totalDays && breakdown.confirmedDays < breakdown.totalDays
    }).length,
    withGaps: studiosData.filter(s => {
      // Save был не во все дни, но хотя бы в один день был
      const breakdown = getStudioDayBreakdown(s, totalDaysInPeriod)
      return breakdown.savedDays > 0 && breakdown.savedDays < breakdown.totalDays
    }).length,
    neverFilled: studiosData.filter(s => {
      // Save не нажимали ни разу за период
      const breakdown = getStudioDayBreakdown(s, totalDaysInPeriod)
      return breakdown.savedDays === 0
    }).length,
    noPlan: noPlanStudioIds.length
  }

  return (
    <div className="space-y-6">
      {/* Заголовок */}
      <div className="text-center">
        <h1 className="text-2xl font-bold mb-2">Аналитика по студиям</h1>
        {selectedPeriod && (
          <p className="text-muted-foreground">
            Период: {new Date(selectedPeriod.period_start).toLocaleDateString('ru-RU')} - {new Date(selectedPeriod.period_end).toLocaleDateString('ru-RU')}
          </p>
        )}
      </div>

      {/* Сводная статистика */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <Card className="p-2 bg-green-100 border-green-200">
          <div className="flex items-center justify-center gap-2">
            <div className="text-xl font-bold text-green-800">{summaryStats.allConfirmed}</div>
            <div className="text-xs text-green-800">Подтверждены</div>
          </div>
        </Card>
        <Card className="p-2 bg-yellow-100 border-yellow-200">
          <div className="flex items-center justify-center gap-2">
            <div className="text-xl font-bold text-yellow-800">{summaryStats.allFilled}</div>
            <div className="text-xs text-yellow-800">Заполнены</div>
          </div>
        </Card>
        <Card className="p-2 bg-red-100 border-red-200">
          <div className="flex items-center justify-center gap-2">
            <div className="text-xl font-bold text-red-800">{summaryStats.withGaps}</div>
            <div className="text-xs text-red-800">Есть пропуски</div>
          </div>
        </Card>
        <Card className="p-2 bg-gray-100 border-gray-200">
          <div className="flex items-center justify-center gap-2">
            <div className="text-xl font-bold text-gray-800">{summaryStats.neverFilled}</div>
            <div className="text-xs text-gray-800">Не заполнялись</div>
          </div>
        </Card>
        <Card className="p-2 bg-orange-100 border-orange-200">
          <div className="flex items-center justify-center gap-2">
            <div className="text-xl font-bold text-orange-800">{summaryStats.noPlan}</div>
            <div className="text-xs text-orange-800">Нет плана</div>
          </div>
        </Card>
      </div>

      {/* Календарь аналитики */}
      <Card>
        <div className="flex items-center gap-2 mb-4">
          <Calendar className="w-5 h-5" />
          <h2 className="text-lg font-medium">Календарь заполнения</h2>
        </div>
        
        {/* Легенда календаря */}
        <div className="flex flex-wrap gap-3 mb-4 text-xs">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-green-100 rounded border border-green-200"></div>
            <span>Все подтвердили</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-yellow-100 rounded border border-yellow-200"></div>
            <span>Заполнили, ждут подтверждения</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-red-100 rounded border border-red-200"></div>
            <span>Есть незаполненные</span>
          </div>
        </div>
        
        {calendarLoading ? (
          <div className="animate-pulse">
            <div className="grid grid-cols-7 gap-1">
              {Array.from({ length: 35 }, (_, i) => (
                <div key={i} className="h-10 bg-muted rounded"></div>
              ))}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-7 gap-1 text-center">
            {/* Дни недели начинаются с четверга */}
            {['Чт', 'Пт', 'Сб', 'Вс', 'Пн', 'Вт', 'Ср'].map(day => (
              <div key={day} className="p-2 text-sm font-medium text-muted-foreground">
                {day}
              </div>
            ))}
            
            {selectedPeriod && (() => {
              const dates = getDatesInPeriod()
              if (dates.length === 0) return null
              
              const firstDate = new Date(dates[0])
              // День недели первой даты (0 = воскресенье, 1 = понедельник, ..., 6 = суббота)
              let dayOfWeek = firstDate.getDay()
              // Преобразуем в систему где четверг = 0
              let offset = (dayOfWeek + 3) % 7 // четверг становится 0
              
              return (
                <>
                  {/* Пустые ячейки до первого дня периода */}
                  {Array.from({ length: offset }).map((_, i) => (
                    <div key={`empty-${i}`} className="p-2"></div>
                  ))}
                  
                  {/* Дни периода */}
                  {dates.map(date => {
                    const dateObj = new Date(date)
                    const day = dateObj.getDate()
                    const status = getStatusForDay(date)
                    const isSelected = date === selectedDate
                    const isToday = date === getLocalIsoDate()
                    
                    // Определяем цвет фона в зависимости от статуса
                    let backgroundClass = 'hover:bg-muted'
                    let textClass = ''
                    let borderClass = ''
                    
                    if (isSelected) {
                      // Выбранный день - всегда показываем как выбранный
                      backgroundClass = 'bg-primary'
                      textClass = 'text-primary-foreground'
                    } else if (status === 'confirmed') {
                      // Подтверждено - светло-зеленый
                      backgroundClass = 'bg-green-100'
                      textClass = 'text-green-700'
                    } else if (status === 'partial') {
                      // Не подтверждено - светло-желтый
                      backgroundClass = 'bg-yellow-100'
                      textClass = 'text-yellow-700'
                    } else if (status === 'missing') {
                      // Не все заполнили - светло-красный
                      backgroundClass = 'bg-red-100'
                      textClass = 'text-red-700'
                    }
                    
                    // Сегодняшний день получает тонкую серую рамку поверх основного цвета
                    if (isToday && !isSelected) {
                      borderClass = 'border border-muted-foreground/40'
                    }
                    
                    return (
                      <button
                        key={date}
                        onClick={() => setSelectedDate(date)}
                        className={`
                          relative p-2 text-sm rounded-lg transition-colors
                          ${backgroundClass} ${textClass} ${borderClass}
                        `}
                      >
                        {day}
                      </button>
                    )
                  })}
                </>
              )
            })()}
          </div>
        )}
      </Card>

      {/* Фильтры и поиск */}
      <Card>
        <div className="space-y-4">
          {/* Поиск */}
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Поиск по студии или городу..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
          </div>

          {/* Фильтр по категориям */}
          <div>
            <label className="block text-sm font-medium mb-2">Категория студий</label>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setFilters(prev => ({ 
                  ...prev, 
                  categoryFilters: prev.categoryFilters.includes('fully_confirmed')
                    ? prev.categoryFilters.filter(c => c !== 'fully_confirmed')
                    : [...prev.categoryFilters, 'fully_confirmed']
                }))}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                  filters.categoryFilters.includes('fully_confirmed')
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-100 text-gray-400'
                }`}
              >
                Подтверждены
              </button>
              <button
                onClick={() => setFilters(prev => ({ 
                  ...prev, 
                  categoryFilters: prev.categoryFilters.includes('fully_filled')
                    ? prev.categoryFilters.filter(c => c !== 'fully_filled')
                    : [...prev.categoryFilters, 'fully_filled']
                }))}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                  filters.categoryFilters.includes('fully_filled')
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-gray-100 text-gray-400'
                }`}
              >
                Заполнены
              </button>
              <button
                onClick={() => setFilters(prev => ({ 
                  ...prev, 
                  categoryFilters: prev.categoryFilters.includes('with_gaps')
                    ? prev.categoryFilters.filter(c => c !== 'with_gaps')
                    : [...prev.categoryFilters, 'with_gaps']
                }))}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                  filters.categoryFilters.includes('with_gaps')
                    ? 'bg-red-100 text-red-800'
                    : 'bg-gray-100 text-gray-400'
                }`}
              >
                Есть пропуски
              </button>
              <button
                onClick={() => setFilters(prev => ({ 
                  ...prev, 
                  categoryFilters: prev.categoryFilters.includes('never_filled')
                    ? prev.categoryFilters.filter(c => c !== 'never_filled')
                    : [...prev.categoryFilters, 'never_filled']
                }))}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                  filters.categoryFilters.includes('never_filled')
                    ? 'bg-gray-100 text-gray-800'
                    : 'bg-gray-100 text-gray-400'
                }`}
              >
                Не заполнялись
              </button>
              <button
                onClick={() => setFilters(prev => ({
                  ...prev,
                  categoryFilters: prev.categoryFilters.includes('no_plan')
                    ? prev.categoryFilters.filter(c => c !== 'no_plan')
                    : [...prev.categoryFilters, 'no_plan']
                }))}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                  filters.categoryFilters.includes('no_plan')
                    ? 'bg-orange-100 text-orange-800'
                    : 'bg-gray-100 text-gray-400'
                }`}
              >
                Нет плана
              </button>
            </div>
            {filters.categoryFilters.length > 0 && (
              <div className="mt-2 text-xs text-muted-foreground">
                Выбрано: {filters.categoryFilters.length}
              </div>
            )}
          </div>

          {/* Сортировка */}
          <div>
            <label className="block text-sm font-medium mb-2">Сортировка</label>
            <select
              value={`${filters.sortBy}-${filters.sortOrder}`}
              onChange={(e) => {
                const [sortBy, sortOrder] = e.target.value.split('-') as [typeof filters.sortBy, typeof filters.sortOrder]
                setFilters(prev => ({ ...prev, sortBy, sortOrder }))
              }}
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            >
              <option value="completion_percentage-asc">Заполнение от низкого к высокому</option>
              <option value="completion_percentage-desc">Заполнение от высокого к низкому</option>
              <option value="last_entry_date-desc">Дата последнего ввода (новые)</option>
            </select>
          </div>

          <div className="flex justify-end">
            <button
              onClick={exportData}
              className="flex items-center gap-2 px-4 py-2 bg-fixed-green-600 text-white rounded-lg hover:bg-fixed-green-700"
            >
              <Download className="w-4 h-4" />
              Экспорт CSV
            </button>
          </div>
        </div>
      </Card>

      {/* Таблица данных */}
      <Card>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium">
              Студии ({filteredData.length})
            </h3>
          </div>

          <div className="space-y-2">
            {filteredData.map((studio) => {
              const hasNoPlan = noPlanStudioIdsSet.has(studio.studio_internal_id)
              const breakdown = getStudioDayBreakdown(studio, totalDaysInPeriod)
              return (
                <div 
                  key={studio.studio_internal_id}
                  className="p-4 border border-border rounded-lg hover:bg-muted/20 cursor-pointer transition-colors"
                  onClick={() => {
                    setSelectedStudio(studio)
                    const selected = studios.find((s) => s.studio_internal_id === studio.studio_internal_id)
                    if (selected?.partner_number === null || selected?.partner_number === undefined) {
                      navigate('/partners')
                      return
                    }
                    navigate(`/partners/${selected.partner_number}/studios/${studio.studio_internal_id}/metrics`)
                 }}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {/* Город как заголовок */}
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium text-base">{studio.city_name}</h4>
                        {hasNoPlan && (
                          <span className="inline-flex items-center rounded-full border border-orange-300 bg-orange-100 px-2 py-0.5 text-[11px] font-medium text-orange-800">
                            Нет плана
                          </span>
                        )}
                      </div>
                      
                      {/* Номер студии и адрес */}
                      <p className="text-sm text-muted-foreground mb-3">
                        {studio.studio_number} • {studio.address || 'Адрес не указан'}
                      </p>

                      {/* Прямоугольники со статистикой */}
                      <div className="flex items-center gap-2">
                        {/* Зеленый - подтвержденные дни */}
                        <div className="w-12 h-8 bg-green-100 border border-green-200 rounded flex items-center justify-center">
                          <span className="text-green-700 font-bold text-sm">
                            {breakdown.confirmedDays}
                          </span>
                        </div>
                        
                        {/* Желтый - заполненные без подтверждения */}
                        <div className="w-12 h-8 bg-yellow-100 border border-yellow-200 rounded flex items-center justify-center">
                          <span className="text-yellow-700 font-bold text-sm">
                            {breakdown.filledUnconfirmedDays}
                          </span>
                        </div>
                        
                        {/* Красный - незаполненные дни */}
                        <div className="w-12 h-8 bg-red-100 border border-red-200 rounded flex items-center justify-center">
                          <span className="text-red-700 font-bold text-sm">
                            {breakdown.missingDays}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <ChevronLeft className="w-4 h-4 text-muted-foreground rotate-180 flex-shrink-0 mt-1" />
                  </div>
                </div>
              )
            })}

            {filteredData.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                {searchTerm 
                  ? 'Нет студий, соответствующих фильтрам'
                  : 'Нет данных для отображения'
                }
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Навигационные кнопки */}
      <div className="mt-8">
        {/* Bottom padding to prevent content from being hidden behind footer */}
        <div className="h-20"></div>
      </div>
      
      <ManagementCompanyNavigation />
    </div>
  )
}
